import React, { FC, useState, useEffect } from "react";
import { Form, message } from "antd";
import {
  EnvironmentOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import FormInput from "../../../components/FormInput/FormInput";
import { BASE_URL } from "../../../constants/urlConstants";
import "./AddPincode.css";

// ─── Config types ────────────────────────────────────────────────────────────

interface ConfigField {
  key: string;
  label: string;
  apiKey: string; // the key used in GET /api/v1/config/{key}
}

const CONFIG_FIELDS: ConfigField[] = [
  { key: "retailer", label: "Retailer Max", apiKey: "retailer_max" },
  { key: "dist",     label: "Distributor Max", apiKey: "dist_max"  },
];

// ─── ConfigRow ───────────────────────────────────────────────────────────────

interface ConfigRowProps {
  label: string;
  apiKey: string;
}

const ConfigRow: FC<ConfigRowProps> = ({ label, apiKey }) => {
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const [inputValue,   setInputValue]   = useState<string>("");
  const [editing,      setEditing]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [fetchingCfg,  setFetchingCfg]  = useState(true);
  const [saved,        setSaved]        = useState(false);

  // Fetch current value on mount
  useEffect(() => {
    const load = async () => {
      try {
        const res  = await fetch(`${BASE_URL}/api/v1/config/${apiKey}`);
        if (!res.ok) throw new Error();
        const text = await res.text();
        setCurrentValue(text);
        setInputValue(text);
      } catch {
        message.error(`Failed to load ${label}`);
        setCurrentValue("—");
      } finally {
        setFetchingCfg(false);
      }
    };
    load();
  }, [apiKey, label]);

  const handleEdit = () => {
    setInputValue(currentValue ?? "");
    setEditing(true);
    setSaved(false);
  };

  const handleSave = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isNaN(Number(trimmed))) {
      message.error("Please enter a valid number");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/api/v1/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: apiKey, value: trimmed }),
      });
      if (!res.ok) throw new Error();
      setCurrentValue(trimmed);
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      message.error(`Failed to update ${label}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setInputValue(currentValue ?? "");
  };

  return (
    <div className="cfg-row">
      <span className="cfg-label">{label}</span>

      {fetchingCfg ? (
        <span className="cfg-loading"><LoadingOutlined /> Loading…</span>
      ) : editing ? (
        <input
          className="cfg-input"
          type="number"
          min={0}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          autoFocus
        />
      ) : (
        <span className="cfg-value">{currentValue}</span>
      )}

      <div className="cfg-actions">
        {saved && !editing && (
          <span className="cfg-saved-badge">
            <CheckCircleOutlined /> Saved
          </span>
        )}

        {editing ? (
          <>
            <button
              className="cfg-btn cfg-btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <LoadingOutlined /> : <SaveOutlined />}
              {saving ? " Saving…" : " Save"}
            </button>
            <button className="cfg-btn cfg-btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
          </>
        ) : (
          <button className="cfg-btn cfg-btn-edit" onClick={handleEdit}>
            <EditOutlined /> Edit
          </button>
        )}
      </div>
    </div>
  );
};

// ─── AddPincode (main page) ───────────────────────────────────────────────────

const AddPincode: FC = () => {
  const [form] = Form.useForm();
  const [loading,          setLoading]          = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [resolved,         setResolved]         = useState(false);

  /* ── PINCODE → CITY / STATE ── */
  const fetchCityState = async (pincode: string) => {
    if (!/^\d{6}$/.test(pincode)) return;
    setFetchingLocation(true);
    setResolved(false);
    form.setFieldsValue({ city: "", state: "" });
    try {
      const res  = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await res.json();
      if (!data?.[0]?.PostOffice?.length) { message.error("Invalid pincode"); return; }
      const po = data[0].PostOffice[0];
      form.setFieldsValue({ city: po.District, state: po.State });
      setResolved(true);
    } catch {
      message.error("Unable to fetch city/state");
    } finally {
      setFetchingLocation(false);
    }
  };

  /* ── SUBMIT ── */
  const onFinish = async (values: any) => {
    try {
      if (!values.city || !values.state) { message.error("City and State not resolved"); return; }
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/v1/order/pincode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pincode: values.pincode, city: values.city, state: values.state, isActive: true }),
      });
      if (!res.ok) throw new Error("Failed to add pincode");
      message.success("Pincode added successfully!");
      form.resetFields();
      setResolved(false);
    } catch (err: any) {
      message.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-page">

      {/* ── Top bar ── */}
      <div className="ap-topbar">
        <EditOutlined className="ap-topbar-icon" />
        <span className="ap-topbar-title">Manage System</span>
      </div>

      {/* ── Breadcrumb ── */}
      <div className="ap-breadcrumb">Dashboard &rsaquo; Manage Pincodes</div>

      <div className="ap-body">

        {/* ══ CONFIG CARD ══════════════════════════════════════════════════════ */}
        <div className="ap-card cfg-card">
          <div className="ap-card-header">
            <EditOutlined />
            <span>System Config</span>
          </div>
          <div className="ap-card-body">
            <p className="cfg-hint">
              Changes apply immediately after saving.
            </p>
            {CONFIG_FIELDS.map((f) => (
              <ConfigRow key={f.key} label={f.label} apiKey={f.apiKey} />
            ))}
          </div>
        </div>

        {/* ══ PINCODE CARD ═════════════════════════════════════════════════════ */}
        <div className="ap-card">
          <div className="ap-card-header">
            <EnvironmentOutlined />
            <span>Add New Pincode</span>
          </div>

          <div className="ap-card-body">
            <div className="ap-hint">
              Enter a 6-digit pincode — city and state will be auto-filled.
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={(_, all) => {
                const pincode = all.pincode;
                if (!pincode || pincode.length < 6) {
                  form.setFieldsValue({ city: "", state: "" });
                  setResolved(false);
                  return;
                }
                if (pincode.length === 6) fetchCityState(pincode);
              }}
            >
              {/* ── Pincode input ── */}
              <Form.Item
                name="pincode"
                label="Pincode"
                rules={[
                  { required: true, message: "Pincode is required" },
                  { len: 6, message: "Must be 6 digits" },
                ]}
              >
                <div className="ap-pincode-wrap">
                  <input
                    className="ap-pincode-input"
                    maxLength={6}
                    placeholder="e.g. 518002"
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  <span className="ap-pincode-status">
                    {fetchingLocation && <LoadingOutlined className="ap-spin" />}
                    {resolved && !fetchingLocation && <CheckCircleOutlined className="ap-check" />}
                  </span>
                </div>
              </Form.Item>

              {/* ── Auto-filled fields ── */}
              <div className="ap-auto-row">
                <div className={`ap-auto-field${resolved ? " resolved" : ""}`}>
                  <FormInput
                    title="City"
                    name="city"
                    titleSpan={8}
                    wrapperSpan={16}
                    disabled
                    placeholder={fetchingLocation ? "Fetching…" : "Auto-filled"}
                  />
                </div>
                <div className={`ap-auto-field${resolved ? " resolved" : ""}`}>
                  <FormInput
                    title="State"
                    name="state"
                    titleSpan={8}
                    wrapperSpan={16}
                    disabled
                    placeholder={fetchingLocation ? "Fetching…" : "Auto-filled"}
                  />
                </div>
              </div>

              {/* ── Submit ── */}
              <button
                type="submit"
                className={`ap-submit-btn${loading || fetchingLocation ? " disabled" : ""}`}
                disabled={loading || fetchingLocation}
              >
                {loading
                  ? <><LoadingOutlined /> Saving…</>
                  : fetchingLocation
                  ? <><LoadingOutlined /> Resolving Pincode…</>
                  : <><EnvironmentOutlined /> Add Pincode</>
                }
              </button>
            </Form>
          </div>
        </div>
        {/* ═════════════════════════════════════════════════════════════════════ */}

      </div>
    </div>
  );
};

export default AddPincode;